import stylesheet from './PaginatedContent.scss?inline';

import CustomComponent from '../../base/customComponent/CustomComponent';
import {getSlotElements, getSlotHtml} from '../../util/slot';
import {getAttributeValueForCurrentBreakpoint} from "../../util/breakpoints";

const FILTER_SLOTS = ['search', 'per-page', 'sort', 'filters', 'sidebar'];
const REFRESHED_SLOTS = ['sidebar', 'filters', 'chips', 'pagination', 'pagination-text'];

class NXTPaginatedContent extends CustomComponent {

    private container: HTMLElement;
    private bodyEl: HTMLElement;
    private defaultSlotEl: HTMLSlotElement;

    isFetchPending = false;
    isFetching = false;
    page = 1;
    private pauseChangeEvents = false;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get api(): string {
        return this.getAttribute('api') ?? '';
    }

    get pageParam(): string {
        return this.getAttribute('page-param') ?? 'page';
    }

    get labelLoading(): string {
        return this.getAttribute('label-loading') ?? 'Loading';
    }

    get limitForCurrentViewport(): number {
        const value = getAttributeValueForCurrentBreakpoint(this, 'limit', true, '12');
        return value ? Number.parseInt(value, 10) : 12;
    }

    get initialLoad(): boolean {
        return this.getAttribute('initial') === 'true';
    }

    render() {
        this.container = document.createElement('div');
        this.container.classList.add('paginated-content');
        this.container.innerHTML = `
            <div class="sidebar"><slot name="sidebar"></slot></div>
            <div class="toolbar">
                <div class="search"><slot name="search"></slot></div>
                <div class="per-page"><slot name="per-page"></slot></div>
                <div class="filters"><slot name="filters"></slot></div>
                <div class="sort"><slot name="sort"></slot></div>
                <div class="result-text"><slot name="pagination-text"></slot></div>
            </div>
            <div class="chips"><slot name="chips"></slot></div>
            <div class="body">
                <slot></slot>
                <div class="loading" aria-live="polite" aria-busy="true">
                    <slot name="loading">
                        <nxt-loading-indicator label="${this.labelLoading}"></nxt-loading-indicator>
                    </slot>
                </div>
            </div>
            <div class="pagination"><slot name="pagination"></slot></div>
        `;

        this.setHasSlottedToParent(this.container);

        this.bodyEl = this.container.querySelector('.body');
        this.defaultSlotEl = this.container.querySelector('slot:not([name])');
        this.defaultSlotEl.addEventListener('slotchange', () => this.updateEmptyState());

        this.addEventListener('change', () => {
            if (this.pauseChangeEvents) {
                return;
            }
            this.page = 1;
            this.fetchData();
        }, true);

        this.addEventListener('clear-filters', () => this.clearAllFilters());
        this.addEventListener('click', (ev) => {
            const target = ev.target as HTMLElement;
            if (target.closest?.('[data-clear-filters]')) {
                ev.preventDefault();
                this.clearAllFilters();
                return;
            }
            const chip = target.closest?.<HTMLElement>('[data-filter-name]');
            if (chip) {
                ev.preventDefault();
                this.removeFilterValue(chip.dataset.filterName ?? '', chip.dataset.filterValue ?? '');
            }
        });

        const paginationEl = this.querySelector('[slot="pagination"]');
        paginationEl?.addEventListener('prev', () => {
            this.prevPage();
            this.fetchData();
        });
        paginationEl?.addEventListener('next', () => {
            this.nextPage();
            this.fetchData();
        });

        if (this.initialLoad) {
            this.fetchData();
        }

        return this.container;
    }

    nextPage() {
        this.page++;
    }

    prevPage() {
        this.page--;
        if (this.page < 1) {
            this.page = 1;
        }
    }

    private filterControls(): (Element & { value: string | string[] })[] {
        const controls: Element[] = [];
        FILTER_SLOTS.forEach((slot) => {
            this.querySelectorAll(`[slot="${slot}"]`).forEach((el) => {
                if (el.hasAttribute('name')) {
                    controls.push(el);
                }
                controls.push(...el.querySelectorAll('[name]'));
            });
        });
        return controls as (Element & { value: string | string[] })[];
    }

    collectParams(): { [k: string]: string } {
        const params: { [k: string]: string } = {};
        this.filterControls().forEach((control) => {
            const name = control.getAttribute('name');
            const rawValue = control.value;
            const value = Array.isArray(rawValue) ? rawValue.join(',') : rawValue;
            if (name && value) {
                params[name] = value;
            }
        });
        params[this.pageParam] = this.page.toString();
        params['limit'] = this.limitForCurrentViewport.toString();
        return params;
    }

    clearAllFilters() {
        this.pauseChangeEvents = true;
        this.filterControls().forEach((control) => this.resetControl(control));
        this.pauseChangeEvents = false;
        this.page = 1;
        this.fetchData();
    }

    removeFilterValue(name: string, value: string) {
        const control = this.filterControls().find((c) => c.getAttribute('name') === name);
        if (!control) {
            return;
        }
        this.pauseChangeEvents = true;
        const current = control.value;
        this.setControlValue(control, Array.isArray(current) ? current.filter((v) => v !== value) : '');
        this.pauseChangeEvents = false;
        this.page = 1;
        this.fetchData();
    }

    private resetControl(control: Element & { value: string | string[] }) {
        this.setControlValue(control, Array.isArray(control.value) ? [] : '');
    }

    private setControlValue(control: Element & { value: string | string[] }, value: string | string[]) {
        try {
            control.value = value;
        } catch {
            // Getter-only control (e.g. a native select wrapper) — nothing to unset.
        }
    }

    buildApiUrl(url: string, query: { [k: string]: string }): string {
        const apiUrl = new URL(url, location.href);
        const params = apiUrl.searchParams;
        for (const key of Object.keys(query)) {
            if (query[key]) {
                params.set(key, query[key]);
            }
        }
        return apiUrl.toString();
    }

    fetchData() {
        if (this.isFetching) {
            this.isFetchPending = true;
            return;
        }
        this.isFetching = true;
        this.bodyEl?.classList.add('is-fetching');
        fetch(this.buildApiUrl(this.api, this.collectParams())).then(async (response) => {
            if (response.ok) {
                const responseMarkup = document.createElement('div');
                responseMarkup.innerHTML = await response.text();
                const sidebarState = this.snapshotSidebarState();
                REFRESHED_SLOTS.forEach((slot) => this.replaceNamedSlot(responseMarkup, slot));
                this.replaceDefaultSlotContents(getSlotElements(responseMarkup));
                this.restoreSidebarState(sidebarState);
                this.setHasSlottedToParent(this.container, true);
            }
        }).finally(() => {
            this.isFetching = false;
            this.bodyEl?.classList.remove('is-fetching');
            if (this.isFetchPending) {
                this.isFetchPending = false;
                this.fetchData();
            }
        });
    }

    private snapshotSidebarState(): Map<string, { open: boolean; expanded: boolean }> {
        const state = new Map<string, { open: boolean; expanded: boolean }>();
        this.querySelectorAll('[slot="sidebar"] [name]').forEach((el) => {
            state.set(el.getAttribute('name'), {open: el.hasAttribute('open'), expanded: el.hasAttribute('expanded')});
        });
        return state;
    }

    private restoreSidebarState(state: Map<string, { open: boolean; expanded: boolean }>) {
        this.querySelectorAll('[slot="sidebar"] [name]').forEach((el) => {
            const saved = state.get(el.getAttribute('name'));
            if (saved) {
                el.toggleAttribute('open', saved.open);
                el.toggleAttribute('expanded', saved.expanded);
            }
        });
    }

    private replaceNamedSlot(responseMarkup: HTMLElement, slotName: string) {
        const slotContainer = this.querySelector(`[slot="${slotName}"]`);
        const newHtml = getSlotHtml(responseMarkup, slotName);
        if (slotContainer && newHtml !== null) {
            slotContainer.innerHTML = newHtml;
        }
    }

    private updateEmptyState() {
        const isEmpty = this.defaultSlotEl.assignedElements().length === 0;
        this.bodyEl?.classList.toggle('is-empty', isEmpty);
    }

}

customElements.define('nxt-paginated-content', NXTPaginatedContent);
